(() => {
  if (window.AWAProductQuoteModal) return;

  const selector = '[data-awa-product-quote-open]';

  class AWAProductQuoteModal {
    constructor() {
      this.dialog = document.querySelector('[data-awa-product-quote-dialog]');
      if (!this.dialog) return;
      this.lastTrigger = null;
      this.purchase = this.dialog.querySelector('[data-awa-product-quote-purchase]');
      this.purchaseInputs = [...this.dialog.querySelectorAll('[name="contact[Quote Type]"]')];
      this.messageInput = this.dialog.querySelector('[data-awa-product-quote-message]');
      this.bind();
      // Errors: reopen so the customer can fix them. Success: the site-wide AWA thank-you pop-up
      // (awa-thanks) takes over, so this dialog stays closed to avoid two pop-ups.
      if (this.dialog.querySelector('[data-awa-product-quote-errors]')) {
        this.open();
      } else if (this.dialog.querySelector('[data-awa-product-quote-success]') && !document.querySelector('[data-awa-ty]')) {
        this.open();
      }
    }

    bind() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest(selector);
        if (trigger) {
          event.preventDefault();
          this.open(trigger);
          return;
        }

        if (event.target.closest('[data-awa-product-quote-close]')) {
          event.preventDefault();
          this.close();
        }
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('awa-product-quote-open');
        this.lastTrigger?.focus?.();
      });
    }

    value(trigger, name) {
      return String(trigger?.dataset?.[name] || '').trim();
    }

    setText(selectorName, value) {
      const element = this.dialog.querySelector(selectorName);
      if (element) element.textContent = value || '';
    }

    setField(key, value) {
      const input = this.dialog.querySelector(`[data-awa-product-quote-field="${key}"]`);
      if (input) input.value = value || '';
    }

    setOptionalField(key, value) {
      const input = this.dialog.querySelector(`[data-awa-product-quote-field="${key}"]`);
      if (!input) return;
      input.value = value || '';
      input.disabled = !value;
    }

    configurePurchaseType(trigger) {
      const installable = this.value(trigger, 'productInstallable') === 'true';

      if (this.purchase) this.purchase.hidden = !installable;
      this.purchaseInputs.forEach((input) => {
        input.checked = false;
        input.required = installable;
        input.disabled = !installable;
      });

      if (this.messageInput) {
        this.messageInput.placeholder = installable
          ? 'Tell us what you need — installation details, quantity, delivery location or any questions.'
          : 'Tell us what you need — quantity, delivery location or any questions.';
      }
    }

    populate(trigger) {
      if (!trigger) return;

      const title = this.value(trigger, 'productTitle');
      const productId = this.value(trigger, 'productId');
      const sku = this.value(trigger, 'productSku');
      const price = this.value(trigger, 'productPrice');
      const path = this.value(trigger, 'productUrl');
      const image = this.value(trigger, 'productImage');
      let url = path;

      try {
        url = path ? new URL(path, window.location.origin).href : window.location.href;
      } catch (_) {}

      this.setText('[data-awa-product-quote-title]', title || 'Selected product');
      this.setText('[data-awa-product-quote-sku]', sku ? `SKU: ${sku}` : '');
      this.setText('[data-awa-product-quote-price]', price);

      this.setField('product', title);
      this.setField('product_id', productId);
      this.setField('sku', sku);
      this.setField('url', url);
      this.setField('price', price);
      this.configurePurchaseType(trigger);

      // Quote-only Evap product card: include the customer's selected options
      // in the enquiry, without affecting normal product quotes or cart.
      this.setOptionalField('evap_install_option', this.value(trigger, 'evapInstallationOption'));
      this.setOptionalField('evap_install_service', this.value(trigger, 'evapInstallationService'));
      this.setOptionalField('evap_roof_dropper', this.value(trigger, 'evapRoofDropper'));
      this.setOptionalField('evap_controller', this.value(trigger, 'evapController'));
      this.setOptionalField('evap_controller_price', this.value(trigger, 'evapControllerCatalogPrice'));

      // Dedicated installation enquiry cards may preselect installation and
      // carry the postcode the customer just checked. Normal quote buttons
      // remain unchanged.
      const preferredQuoteType = this.value(trigger, 'quotePreselect');
      if (preferredQuoteType) {
        const preferred = this.purchaseInputs.find(input => input.value === preferredQuoteType && !input.disabled);
        if (preferred) preferred.checked = true;
        const typedPostcode = this.value(trigger, 'installationPostcode');
        const postcodeInput = this.dialog.querySelector('#AwaQuotePostcode');
        if (postcodeInput) postcodeInput.value = /^[0-9]{4}$/.test(typedPostcode) ? typedPostcode : '';
      }

      const imageWrap = this.dialog.querySelector('[data-awa-product-quote-image-wrap]');
      const imageElement = this.dialog.querySelector('[data-awa-product-quote-image]');
      if (imageWrap && imageElement) {
        if (image) {
          imageElement.src = image;
          imageElement.alt = title;
          imageWrap.hidden = false;
        } else {
          imageElement.removeAttribute('src');
          imageElement.alt = '';
          imageWrap.hidden = true;
        }
      }
    }

    open(trigger = null) {
      if (!this.dialog) return;
      if (trigger) {
        this.lastTrigger = trigger;
        this.populate(trigger);
      }
      document.documentElement.classList.add('awa-product-quote-open');
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      requestAnimationFrame(() => {
        this.dialog.querySelector('input:not([type="hidden"]), textarea')?.focus?.();
      });
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      document.documentElement.classList.remove('awa-product-quote-open');
    }
  }

  const init = () => {
    if (window.AWAProductQuoteModal) return;
    window.AWAProductQuoteModal = new AWAProductQuoteModal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();